import { Link } from "react-router-dom";
import { useCourseIndex, useCourseManifest, type CourseManifest } from "@/lib/course";
import { useProgress, coursePercent, lessonId, resumeLocation } from "@/lib/progress";
import { cn } from "@/lib/cn";

interface Props {
  /** Called after the user clicks a course row. Used by the drawer to close
   * itself; pages can pass a no-op. */
  onNavigate?: () => void;
}

/** Shared rendering of the course catalog. Used by CoursesCatalogDrawer (slide
 * drawer chrome) and the /courses landing page (full-page chrome). */
export function CoursesCatalogList({ onNavigate }: Props): JSX.Element {
  const index = useCourseIndex();

  if (index.status === "loading") {
    return (
      <div className="space-y-2 py-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            role="status"
            aria-live="polite"
            className="rounded-control border border-border bg-muted/30 h-10 motion-safe:animate-pulse"
          >
            <span className="sr-only">Loading courses</span>
          </div>
        ))}
      </div>
    );
  }
  if (index.status === "error") {
    return <p className="text-sm text-destructive py-4">Failed to load courses.</p>;
  }
  if (index.courses.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No courses available.</p>;
  }
  return (
    <ul role="list" className="list-none p-0 m-0">
      {index.courses.map((c) => (
        <li key={c.slug}>
          <CourseRow
            slug={c.slug}
            {...(onNavigate ? { onNavigate } : {})}
          />
        </li>
      ))}
    </ul>
  );
}

function CourseRow({ slug, onNavigate }: { slug: string; onNavigate?: () => void }): JSX.Element {
  const manifestState = useCourseManifest(slug);
  const progress = useProgress(manifestState.status === "ready" ? manifestState.manifest.slug : "");

  if (manifestState.status === "loading") {
    return <div className="h-16 rounded-control bg-muted/30 motion-safe:animate-pulse mb-2" />;
  }
  if (manifestState.status === "error") {
    return (
      <div className="text-xs text-destructive py-2 mb-2">
        Failed to load {slug}.
      </div>
    );
  }
  return (
    <CourseRowReady
      manifest={manifestState.manifest}
      progress={progress}
      {...(onNavigate ? { onNavigate } : {})}
    />
  );
}

type CourseStatus = "not-started" | "in-progress" | "completed";

function deriveCourseStatus(
  progress: ReturnType<typeof useProgress>,
  totalLessons: number
): CourseStatus {
  if (progress.status !== "ready") return "not-started";
  const completed = progress.progress.completedLessons.length;
  const visited = progress.progress.lastVisited !== null;
  if (totalLessons > 0 && completed >= totalLessons) return "completed";
  if (completed === 0 && !visited) return "not-started";
  return "in-progress";
}

function CourseRowReady({
  manifest,
  progress,
  onNavigate,
}: {
  manifest: CourseManifest;
  progress: ReturnType<typeof useProgress>;
  onNavigate?: () => void;
}): JSX.Element {
  const totalLessons = manifest.chapters.reduce((a, c) => a + c.lessons.length, 0);
  const progressReady = progress.status === "ready";
  const status = deriveCourseStatus(progress, totalLessons);

  const nextIncompleteLoc = (() => {
    if (progress.status !== "ready") return null;
    const completed = new Set(progress.progress.completedLessons);
    for (const ch of manifest.chapters) {
      for (const ls of ch.lessons) {
        if (!completed.has(lessonId(ch.folder, ls.slug))) {
          return { chapter: ch.folder, lesson: ls.slug };
        }
      }
    }
    return null;
  })();

  const resume =
    progress.status === "ready"
      ? resumeLocation(progress.progress, nextIncompleteLoc)
      : null;

  const firstCh = manifest.chapters[0];
  const firstLesson = firstCh?.lessons[0];
  const href = resume
    ? `/courses/${manifest.slug}/${resume.chapter}/${resume.lesson}`
    : firstCh && firstLesson
      ? `/courses/${manifest.slug}/${firstCh.folder}/${firstLesson.slug}`
      : `/courses/${manifest.slug}`;

  const pillCfg: Record<CourseStatus, { label: string; classes: string }> = {
    "not-started": { label: "Not started", classes: "bg-muted text-muted-foreground" },
    "in-progress": {
      label: `In progress (${progress.status === "ready" ? coursePercent(progress.progress, totalLessons) : 0}%)`,
      classes: "bg-primary/15 text-primary-soft",
    },
    completed: { label: "Completed", classes: "bg-success/20 text-success" },
  };
  const pill = progressReady
    ? pillCfg[status]
    : { label: "Loading…", classes: "bg-muted text-muted-foreground" };

  return (
    <Link
      to={href}
      onClick={(e) => {
        if (!progressReady) {
          e.preventDefault();
          return;
        }
        onNavigate?.();
      }}
      aria-disabled={!progressReady}
      className={cn(
        "block rounded-control px-3 py-3 mb-1 transition-colors",
        progressReady ? "hover:bg-muted" : "opacity-60 cursor-wait pointer-events-none",
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-base font-semibold text-foreground leading-snug">
          {manifest.title}
        </span>
        <span
          className={cn(
            "inline-block whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide shrink-0",
            pill.classes
          )}
        >
          {pill.label}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {manifest.chapters.length} chapters · {totalLessons} lessons · ~{manifest.estimatedHours}h
      </p>
    </Link>
  );
}
